/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, Stack, Button,
    TextField, InputAdornment, alpha, Checkbox,
    Tooltip, IconButton, CircularProgress,
} from '@mui/material';
import { m } from 'framer-motion';
import Iconify from 'src/components/Iconify';
import { useSelector, dispatch } from '../../../redux/store';
import { getPracticeTreatmentList } from '../../../redux/slices/practiceTreatment';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(val) {
    const n = parseFloat(val);
    if (Number.isNaN(n) || n <= 0) return null;
    return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const CARD_MOTION = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
};

// ─── TreatmentPickerItem ─────────────────────────────────────────────────────

function TreatmentPickerItem({ treatment, checked, onToggle }) {
    const name = treatment.treatment_item?.name || treatment.name || 'Unknown';
    const category = treatment.treatment_item?.treatment_category?.name || '';
    const price = formatCurrency(treatment.price || treatment.treatment_item?.price);
    const colour = treatment.colour || '#919EAB';

    return (
        <Box
            onClick={onToggle}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: '10px 12px',
                borderRadius: '10px',
                cursor: 'pointer',
                border: '1px solid',
                borderColor: checked ? alpha('#00AB55', 0.4) : 'rgba(145,158,171,0.12)',
                bgcolor: checked ? alpha('#00AB55', 0.04) : '#fff',
                transition: 'all 0.15s ease',
                '&:hover': {
                    borderColor: alpha('#00AB55', 0.3),
                    bgcolor: alpha('#00AB55', 0.02),
                },
            }}
        >
            <Checkbox
                size="small"
                checked={checked}
                onChange={onToggle}
                onClick={(e) => e.stopPropagation()}
                sx={{ p: 0.25, color: '#C4CDD5', '&.Mui-checked': { color: '#00AB55' } }}
            />
            <Box
                sx={{
                    width: 8, height: 8, borderRadius: '50%',
                    bgcolor: colour, flexShrink: 0,
                }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', color: '#212B36' }}>
                    {name}
                </Typography>
                {category && (
                    <Typography variant="caption" sx={{ fontSize: '11px', color: '#919EAB' }}>
                        {category}
                    </Typography>
                )}
            </Box>
            {price && (
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, fontSize: '13px', color: '#00AB55', flexShrink: 0 }}
                >
                    {price}
                </Typography>
            )}
        </Box>
    );
}

TreatmentPickerItem.propTypes = {
    treatment: PropTypes.object.isRequired,
    checked: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

// ─── Main component ──────────────────────────────────────────────────────────

export default function TreatmentPlanStartSelector({ leadDetail, onSelect }) {
    const practiceId = useSelector((state) => state.practiceTreatmentList.practiceId);
    // practiceTreatment slice (NOT practiceTreatmentList) holds the treatments data
    const treatmentsListData = useSelector((state) => state.practiceTreatment.treatmentsList);
    const treatmentsLoading = useSelector((state) => state.practiceTreatment.isLoading);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTreatments, setSelectedTreatments] = useState([]);

    // ─── Load data on mount ──────────────────────────────────────────────────
    useEffect(() => {
        if (!practiceId) return;

        // Load practice treatments for the picker (limit triggers addTreatmentsList in Redux)
        dispatch(getPracticeTreatmentList(practiceId, { limit: 200, offset: 0 }));
    }, [practiceId]);

    // ─── Derived data ────────────────────────────────────────────────────────
    const treatmentsList = useMemo(() => {
        const results = treatmentsListData?.results || [];
        return Array.isArray(results) ? results : [];
    }, [treatmentsListData]);

    const leadTreatments = leadDetail?.lead_treatments || [];

    // Filter treatments by search
    const filteredTreatments = useMemo(() => {
        if (!searchQuery.trim()) return treatmentsList;
        const q = searchQuery.toLowerCase();
        return treatmentsList.filter((t) => {
            const name = (t.treatment_item?.name || '').toLowerCase();
            const cat = (t.treatment_item?.treatment_category?.name || '').toLowerCase();
            return name.includes(q) || cat.includes(q);
        });
    }, [treatmentsList, searchQuery]);

    // Pre-select lead treatments on mount
    useEffect(() => {
        if (leadTreatments.length > 0 && selectedTreatments.length === 0 && treatmentsList.length > 0) {
            // Match lead treatments to practice treatments by name
            const preSelected = [];
            leadTreatments.forEach((lt) => {
                const name = lt.treatment_item?.name;
                if (name) {
                    const match = treatmentsList.find(
                        (pt) => (pt.treatment_item?.name || '').toLowerCase() === name.toLowerCase()
                    );
                    if (match) {
                        preSelected.push({
                            practiceId: match.id,
                            name: match.treatment_item?.name || name,
                            price: lt.custom_price || match.price || lt.treatment_item?.price || '',
                            colour: match.colour || '',
                            category: match.treatment_item?.treatment_category?.name || '',
                            leadTreatmentId: lt.id,
                        });
                    } else {
                        // Lead treatment not in practice list — add anyway
                        preSelected.push({
                            practiceId: null,
                            name: lt.treatment_item?.name || 'Treatment',
                            price: lt.custom_price || lt.treatment_item?.price || '',
                            colour: '',
                            category: '',
                            leadTreatmentId: lt.id,
                        });
                    }
                }
            });
            if (preSelected.length > 0) setSelectedTreatments(preSelected);
        }
    }, [treatmentsList, leadTreatments]);

    // ─── Handlers ────────────────────────────────────────────────────────────
    const toggleTreatment = useCallback((treatment) => {
        const { id } = treatment;
        setSelectedTreatments((prev) => {
            const exists = prev.find((s) => s.practiceId === id);
            if (exists) return prev.filter((s) => s.practiceId !== id);
            return [...prev, {
                practiceId: id,
                name: treatment.treatment_item?.name || '',
                price: treatment.price || '',
                colour: treatment.colour || '',
                category: treatment.treatment_item?.treatment_category?.name || '',
                leadTreatmentId: null,
            }];
        });
    }, []);

    const isTreatmentSelected = useCallback((treatment) => (
        selectedTreatments.some((s) => s.practiceId === treatment.id)
    ), [selectedTreatments]);

    const handleContinue = () => {
        if (selectedTreatments.length === 0) return;

        onSelect({
            path: selectedTreatments.some((s) => s.leadTreatmentId) ? 'lead' : 'treatments',
            treatments: selectedTreatments,
            leadTreatments: leadTreatments.filter(
                (lt) => selectedTreatments.some((s) => s.leadTreatmentId === lt.id)
            ),
        });
    };

    const handleScratch = () => {
        onSelect({ path: 'scratch' });
    };

    const canContinue = selectedTreatments.length > 0;

    return (
        <Box sx={{ maxWidth: 640, mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>
            {/* Header */}
            <Box component={m.div} {...CARD_MOTION} sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px', mb: 0.5 }}>
                    Which treatments are included?
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                    Search and select treatments — each becomes a plan option the patient can compare
                </Typography>
            </Box>

            {/* Search bar */}
            <Box component={m.div} {...CARD_MOTION} sx={{ mb: 2.5 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search treatments by name or category..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Iconify icon="eva:search-fill" width={18} sx={{ color: '#919EAB' }} />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                            <InputAdornment position="end">
                                <IconButtonClear onClick={() => setSearchQuery('')} />
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px', fontSize: '13px', bgcolor: '#fff',
                            '& fieldset': { borderColor: 'rgba(145,158,171,0.2)' },
                            '&.Mui-focused fieldset': { borderColor: '#00AB55', borderWidth: '1.5px' },
                        },
                    }}
                />
            </Box>

            {/* Selected treatments with editable prices */}
            {selectedTreatments.length > 0 && (
                <Box component={m.div} {...CARD_MOTION} sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontSize: '12px', fontWeight: 600, color: '#637381', mb: 1, display: 'block' }}>
                        Selected ({selectedTreatments.length}) — adjust prices if needed:
                    </Typography>
                    <Stack spacing={0.75}>
                        {selectedTreatments.map((s, idx) => (
                            <Box
                                key={s.practiceId || s.name}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    p: '8px 12px', borderRadius: '10px',
                                    bgcolor: alpha('#00AB55', 0.04),
                                    border: `1px solid ${alpha('#00AB55', 0.2)}`,
                                }}
                            >
                                {s.colour && (
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.colour || '#919EAB', flexShrink: 0 }} />
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', color: '#212B36' }}>
                                        {s.name}
                                    </Typography>
                                    {s.category && (
                                        <Typography variant="caption" sx={{ fontSize: '10px', color: '#919EAB' }}>
                                            {s.category}
                                        </Typography>
                                    )}
                                </Box>
                                <TextField
                                    size="small"
                                    type="number"
                                    placeholder="Price"
                                    value={s.price || ''}
                                    onChange={(e) => {
                                        setSelectedTreatments((prev) => {
                                            const next = [...prev];
                                            next[idx] = { ...next[idx], price: e.target.value };
                                            return next;
                                        });
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography sx={{ fontSize: '12px', color: '#637381' }}>£</Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        width: 120, flexShrink: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px', fontSize: '13px', fontWeight: 600, bgcolor: '#fff',
                                            '& fieldset': { borderColor: alpha('#00AB55', 0.2) },
                                            '&.Mui-focused fieldset': { borderColor: '#00AB55', borderWidth: '1.5px' },
                                        },
                                        '& .MuiOutlinedInput-input': { py: '6px', px: '6px' },
                                    }}
                                />
                                <Tooltip title="Remove">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedTreatments((prev) => prev.filter((x) => x !== s));
                                        }}
                                        sx={{ p: 0.25, color: alpha('#00AB55', 0.4), '&:hover': { color: '#FF4842' } }}
                                    >
                                        <Iconify icon="eva:close-fill" width={16} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Lead treatments quick-add banner */}
            {leadTreatments.length > 0 && selectedTreatments.length === 0 && (
                <Box
                    component={m.div}
                    {...CARD_MOTION}
                    sx={{
                        mb: 2, p: 1.5, borderRadius: '10px',
                        bgcolor: alpha('#855CF8', 0.04),
                        border: `1px solid ${alpha('#855CF8', 0.16)}`,
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Iconify icon="solar:tooth-bold-duotone" width={18} sx={{ color: '#855CF8' }} />
                        <Typography variant="body2" sx={{ fontSize: '12px', color: '#637381', flex: 1 }}>
                            This lead has {leadTreatments.length} treatment{leadTreatments.length > 1 ? 's' : ''} on file
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                // Auto-select all lead treatments
                                const toAdd = leadTreatments.map((lt) => {
                                    const match = treatmentsList.find(
                                        (pt) => (pt.treatment_item?.name || '').toLowerCase() === (lt.treatment_item?.name || '').toLowerCase()
                                    );
                                    return {
                                        practiceId: match?.id || null,
                                        name: lt.treatment_item?.name || 'Treatment',
                                        price: lt.custom_price || match?.price || lt.treatment_item?.price || '',
                                        colour: match?.colour || '',
                                        category: match?.treatment_item?.treatment_category?.name || '',
                                        leadTreatmentId: lt.id,
                                    };
                                });
                                setSelectedTreatments(toAdd);
                            }}
                            sx={{
                                fontSize: '11px', fontWeight: 600, textTransform: 'none',
                                borderRadius: '7px', py: '2px', px: '10px',
                                borderColor: alpha('#855CF8', 0.3), color: '#855CF8',
                                '&:hover': { borderColor: '#855CF8', bgcolor: alpha('#855CF8', 0.06) },
                            }}
                        >
                            Add all
                        </Button>
                    </Stack>
                </Box>
            )}

            {/* Treatment list */}
            <Box
                component={m.div}
                {...CARD_MOTION}
                sx={{
                    bgcolor: '#fff', borderRadius: '12px',
                    border: '1px solid rgba(145,158,171,0.16)',
                    maxHeight: 320, overflow: 'auto',
                    mb: 2,
                }}
            >
                {treatmentsLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} sx={{ color: '#00AB55' }} />
                    </Box>
                )}
                {!treatmentsLoading && filteredTreatments.length > 0 && (
                    <Stack spacing={0}>
                        {filteredTreatments.map((treatment) => (
                            <TreatmentPickerItem
                                key={treatment.id}
                                treatment={treatment}
                                checked={isTreatmentSelected(treatment)}
                                onToggle={() => toggleTreatment(treatment)}
                            />
                        ))}
                    </Stack>
                )}
                {!treatmentsLoading && filteredTreatments.length === 0 && (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Iconify icon="eva:search-fill" width={28} sx={{ color: '#C4CDD5', mb: 1 }} />
                        <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '13px' }}>
                            {searchQuery ? 'No treatments match your search' : 'No treatments found'}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Actions */}
            <Stack
                component={m.div}
                {...CARD_MOTION}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 3 }}
            >
                <Button
                    size="small"
                    onClick={handleScratch}
                    sx={{
                        textTransform: 'none', fontWeight: 600, fontSize: '12px',
                        color: '#919EAB',
                        '&:hover': { color: '#637381', bgcolor: 'transparent' },
                    }}
                >
                    Skip — start from scratch
                </Button>
                <Button
                    variant="contained"
                    size="medium"
                    disabled={!canContinue}
                    onClick={handleContinue}
                    endIcon={<Iconify icon="eva:arrow-forward-fill" width={16} />}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '10px',
                        fontWeight: 600,
                        px: 3,
                        bgcolor: '#00AB55',
                        '&:hover': { bgcolor: '#007B40' },
                        '&.Mui-disabled': { bgcolor: 'rgba(0,171,85,0.3)', color: '#fff' },
                    }}
                >
                    Continue with {selectedTreatments.length} treatment{selectedTreatments.length !== 1 ? 's' : ''}
                </Button>
            </Stack>
        </Box>
    );
}

TreatmentPlanStartSelector.propTypes = {
    leadDetail: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
};

// Small helper — clear search icon button
function IconButtonClear({ onClick }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                cursor: 'pointer', display: 'flex', p: 0.25,
                borderRadius: '50%', '&:hover': { bgcolor: 'rgba(145,158,171,0.12)' },
            }}
        >
            <Iconify icon="eva:close-fill" width={16} sx={{ color: '#919EAB' }} />
        </Box>
    );
}

IconButtonClear.propTypes = {
    onClick: PropTypes.func.isRequired,
};
